/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useState, useEffect, useRef } from "react";
import { isMediumDeviceAndUp } from "common/utils/browser";
import { generateUUID } from "common/utils/uuid";
import {
  trackTransfersArtistEditEscalationShown,
  trackTransfersPinClarificationClicked,
  trackTransfersPinCreated,
  trackTransfersPinDeleted,
  trackTransfersPinDeleteFailed,
  trackTransfersPinError,
  trackTransfersPinMessageSubmitted,
  trackTransfersPinNeedsClarification,
  trackTransfersPinReady,
  trackTransfersPinSelected,
  trackTransfersPinUnsupported,
  trackPinSessionGeneratedImageShown
} from "../transfersPinningAnalytics";
import {
  approvePin as approvePinApi,
  rejectPin as rejectPinApi,
  removePin as removePinApi,
  submitPinMessage,
  updatePinInstruction as updatePinInstructionApi
} from "./usePinningSession";

const MIN_MESSAGE_LENGTH = 1;
const MAX_PINS = 1;

export const PIN_COLORS = ["#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#14B8A6"];

/**
 * Gets the effective answer value for a clarification response.
 * @param {object | undefined} response - { selected, custom, isTypingCustom }
 * @returns {string | null}
 */
export function getClarificationAnswer(response: any) {
  if (!response) return null;
  if (response.isTypingCustom) {
    return response.custom?.trim() || null;
  }
  return response.selected || null;
}

/**
 * Core hook for managing pinning panel state and orchestration.
 * All ephemeral state lives here and is garbage-collected when the component unmounts.
 *
 * @param {string | null} sessionId - The current session ID
 * @param {string | null} artworkId - The current artwork ID
 * @returns {object} Panel state, computed values, and handlers
 */
export function usePinningPanel(
  sessionId: any,
  artworkId: any,
  { toast, pendingInitialMessage }: any = {}
) {
  // @ts-expect-error TS(6133): 'setIsDesktop' is declared but its value is never ... Remove this comment to see the full error message
  const [isDesktop, setIsDesktop] = useState(!!isMediumDeviceAndUp()); // eslint-disable-line no-unused-vars
  const [pins, setPins] = useState([]);
  const [activePinId, setActivePinId] = useState(null);
  const [hoveredPinId, setHoveredPinId] = useState(null);
  const [hoveredPinSource, setHoveredPinSource] = useState(null);
  const [composerText, setComposerText] = useState("");
  const [removingPinIds, setRemovingPinIds] = useState(new Set());
  const [pendingDeletePinId, setPendingDeletePinId] = useState(null);
  const [clarificationResponses, setClarificationResponses] = useState({});
  const [mobileView, setMobileView] = useState("list");
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [editingPinId, setEditingPinId] = useState(null);
  const [isUpdatingPinInstruction, setIsUpdatingPinInstruction] = useState(false);
  const [lastEditedPinId, setLastEditedPinId] = useState(null);
  const [unansweredAbove, setUnansweredAbove] = useState(0);
  const [unansweredBelow, setUnansweredBelow] = useState(0);
  const [showPins, setShowPins] = useState(true); // eslint-disable-line no-unused-vars
  const [previewApproval, setPreviewApproval] = useState<{
    pinId: string;
    generatedImageUrl: string;
    instruction?: string;
  } | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isPreviewImageLoaded, setIsPreviewImageLoaded] = useState(false);

  const textareaRef = useRef(null);
  const [pendingCursorPosition, setPendingCursorPosition] = useState(null);
  const clarificationScrollRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const pinsContainerRef = useRef(null);

  const pinsRef = useRef(pins);
  pinsRef.current = pins;
  const removingPinIdsRef = useRef(removingPinIds);
  removingPinIdsRef.current = removingPinIds;

  const savedClarificationResponsesRef = useRef<any>(null);
  const savedPinStateRef = useRef<any>(null);
  const savedMessagesRef = useRef<any>(null);
  const savedComposerTextRef = useRef<any>(null);
  const abortControllerRef = useRef<any>(null);

  function activePin() {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    return pins.find(p => p.id === activePinId) ?? null;
  }

  function editingPin() {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    return pins.find(p => p.id === editingPinId) ?? null;
  }

  function hasCompletedPin() {
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    return pins.some(pin => pin.state === "ready" || pin.state === "unsupported");
  }

  function readyPinsCount() {
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    return pins.filter(pin => pin.state === "ready").length;
  }

  function hasExistingPin() {
    // @ts-expect-error TS(2339): Property 'isNew' does not exist on type 'never'.
    return pins.some(pin => !pin.isNew);
  }

  function hasAiAssistanceOngoing() {
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    return pins.some(pin => pin.state === "processing" || pin.state === "needs_clarification");
  }

  function hasUnfinishedPin() {
    return pins.some(
      pin =>
        // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
        pin.state === "draft" || pin.state === "processing" || pin.state === "needs_clarification"
    );
  }

  function canAddNewPin() {
    return (
      pins.length < MAX_PINS &&
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      (!activePin() || activePin().state === "ready" || activePin().state === "unsupported")
    );
  }

  function canRepositionActivePin() {
    const active = activePin();
    if (!active) return false;
    const { state, messages } = active;
    // @ts-expect-error TS(2339): Property 'length' does not exist on type 'never'.
    return state === "draft" && messages.length === 0;
  }

  function lastAiMessageWithClarifications() {
    const active = activePin();
    if (!active) return null;
    const { messages } = active;
    // @ts-expect-error TS(2339): Property 'length' does not exist on type 'never'.
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      // @ts-expect-error TS(2339): Property 'role' does not exist on type 'never'.
      if (msg?.role === "ai" && msg.clarifications && msg.clarifications.length > 0) return msg;
    }
    return null;
  }

  function pendingPillClarifications() {
    // @ts-expect-error TS(2339): Property 'clarifications' does not exist on type '... Remove this comment to see the full error message
    const clarifications = lastAiMessageWithClarifications()?.clarifications ?? [];
    return clarifications.filter((c: any) => c.status === "asking" && c.options.length > 0);
  }

  function fontClarification() {
    return pendingPillClarifications().find((c: any) => c.type === "font_selection");
  }

  function colorClarification() {
    return pendingPillClarifications().find((c: any) => c.type === "font_color");
  }

  function selectedFontValue() {
    const font = fontClarification();
    if (!font) return undefined;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const response = clarificationResponses[font.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = font.options.find((o: any) => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }

  function selectedColorValue() {
    const color = colorClarification();
    if (!color) return undefined;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const response = clarificationResponses[color.question];
    if (response?.isTypingCustom) return response.custom || undefined;
    if (response?.selected) {
      const opt = color.options.find((o: any) => o.label === response.selected);
      return opt?.value || response.selected;
    }
    return undefined;
  }

  function hasAnyClarificationResponse() {
    return pendingPillClarifications().some((c: any) =>
      // @ts-expect-error TS migration
      getClarificationAnswer(clarificationResponses[c.question])
    );
  }

  function updatePin(pinId: any, updater: any) {
    // @ts-expect-error TS(2345): Argument of type '(prev: never[]) => any[]' is not... Remove this comment to see the full error message
    setPins(prev => prev.map(pin => (pin.id === pinId ? updater(pin) : pin)));
  }

  function deletePin(pinId: any) {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    setPins(prev => prev.filter(p => p.id !== pinId));
    setActivePinId(prev => (prev === pinId ? null : prev));
    setPreviewApproval(prev => (prev?.pinId === pinId ? null : prev));
  }

  function clearRemovingPin(pinId: any) {
    setRemovingPinIds(prev => {
      const next = new Set(prev);
      next.delete(pinId);
      return next;
    });
  }

  function isComposerTextValid(text: any) {
    return text.trim().length >= MIN_MESSAGE_LENGTH;
  }

  function repositionActivePin(coordinates: any) {
    if (!canRepositionActivePin()) return;
    updatePin(activePinId, (pin: any) => ({
      ...pin,
      coordinates
    }));
  }

  function addPin(coordinates: any) {
    if (!canAddNewPin()) return null;

    const newPin = {
      id: generateUUID(),
      coordinates,
      state: "draft",
      messages: [],
      instruction: undefined,
      areasOfInterest: undefined,
      segmentationMaskUrl: undefined,
      highlightAreasEnabled: undefined,
      thinkingText: undefined,
      isNew: true
    };

    // @ts-expect-error TS(2345): Argument of type '(prev: never[]) => { id: string;... Remove this comment to see the full error message
    setPins(prev => [...prev, newPin]);
    // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
    setActivePinId(newPin.id);

    if (!isDesktop) {
      setMobileView("detail");
      setIsPanelExpanded(true);
    }

    if (sessionId && artworkId) {
      trackTransfersPinCreated({
        artworkId,
        sessionId,
        pinId: newPin.id
      });
    }

    return newPin;
  }

  function handleDeletePin(pinId: any) {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    const pin = pins.find(p => p.id === pinId);
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    if (pin?.state === "processing") {
      abortControllerRef.current?.abort();
    }

    if (!sessionId) {
      deletePin(pinId);
      return;
    }

    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    const hasBeenSyncedToBackend = (pin?.messages?.length ?? 0) > 0 || pin?.state !== "draft";

    if (!hasBeenSyncedToBackend) {
      if (artworkId) {
        trackTransfersPinDeleted({
          artworkId,
          sessionId,
          pinId,
          // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
          pinState: pin?.state ?? "draft",
          hadMessages: false
        });
      }
      deletePin(pinId);
      if (!isDesktop) {
        setMobileView("list");
      }
      return;
    }

    if (removingPinIds.has(pinId)) return;

    setRemovingPinIds(prev => new Set(prev).add(pinId));

    removePinApi(sessionId, pinId)
      .then(() => {
        if (artworkId) {
          trackTransfersPinDeleted({
            artworkId,
            sessionId,
            pinId,
            // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
            pinState: pin?.state ?? "draft",
            // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
            hadMessages: (pin?.messages?.length ?? 0) > 0
          });
        }
        deletePin(pinId);
        clearRemovingPin(pinId);
        if (!isDesktop) {
          setMobileView("list");
        }
      })
      .catch(() => {
        if (artworkId) {
          trackTransfersPinDeleteFailed({ artworkId, sessionId, pinId });
        }
        clearRemovingPin(pinId);
        toast?.error({
          title: "Failed to remove pin",
          description: "Something went wrong. Please try again."
        });
      });
  }

  function openDeletePinModal(pinId: any) {
    setPendingDeletePinId(pinId);
  }

  function closeDeletePinModal() {
    setPendingDeletePinId(null);
  }

  function confirmDeletePin() {
    if (!pendingDeletePinId) return;
    handleDeletePin(pendingDeletePinId);
    setPendingDeletePinId(null);
  }

  function openEditPinModal(pinId: any) {
    setEditingPinId(pinId);
  }

  function closeEditPinModal() {
    setEditingPinId(null);
  }

  function handleUpdatePinInstruction(instruction: any) {
    if (!editingPinId || !sessionId) return;

    setIsUpdatingPinInstruction(true);

    updatePinInstructionApi(sessionId, editingPinId, instruction)
      .then(() => {
        updatePin(editingPinId, (pin: any) => ({
          ...pin,
          instruction
        }));
        setEditingPinId(null);
      })
      .catch(() => {
        toast?.error({
          title: "Failed to update pin",
          description: "Something went wrong. Please try again."
        });
      })
      .finally(() => {
        setIsUpdatingPinInstruction(false);
      });
  }

  function handleSelectClarificationOption(question: any, value: any) {
    const prev = clarificationResponses;

    const newResponses = {
      ...prev,
      [question]: {
        selected: value,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        custom: prev[question]?.custom ?? "",
        isTypingCustom: false
      }
    };

    setClarificationResponses(newResponses);

    const currentPin = activePin();
    if (sessionId && artworkId && currentPin) {
      // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
      const userCount = currentPin.messages.filter((m: any) => m.role === "user").length;
      // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
      const aiCount = currentPin.messages.filter((m: any) => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        pinId: currentPin.id,
        optionValue: value || "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "suggested"
      });
    }

    if (value !== null) {
      const clarification = pendingPillClarifications().find((c: any) => c.question === question);
      if (clarification?.type === "finalize_confirmation") {
        handleSubmit(newResponses);
        return;
      }
    }

    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    if (value !== null && !getClarificationAnswer(prev[question])) {
      setTimeout(() => {
        const currentIndex = pendingPillClarifications().findIndex(
          (c: any) => c.question === question
        );
        const reorderedClarifications = [
          ...pendingPillClarifications().slice(currentIndex + 1),
          ...pendingPillClarifications().slice(0, currentIndex)
        ];

        const nextUnanswered = reorderedClarifications.find(
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          c => !getClarificationAnswer(newResponses[c.question])
        );

        if (nextUnanswered && clarificationScrollRef.current) {
          const labelValue = nextUnanswered.label || nextUnanswered.question;
          // @ts-expect-error TS(2339): Property 'querySelector' does not exist on type 'n... Remove this comment to see the full error message
          const targetElement = clarificationScrollRef.current.querySelector(
            `[data-clarification-label="${CSS.escape(labelValue)}"]`
          );
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      }, 100);
    }
  }

  function handleToggleCustomInput(question: any) {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        selected: prev[question]?.selected ?? null,
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        custom: prev[question]?.custom ?? "",
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        isTypingCustom: !prev[question]?.isTypingCustom
      }
    }));

    const currentPin = activePin();
    if (sessionId && artworkId && currentPin) {
      // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
      const userCount = currentPin.messages.filter((m: any) => m.role === "user").length;
      // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
      const aiCount = currentPin.messages.filter((m: any) => m.role === "ai").length;
      trackTransfersPinClarificationClicked({
        artworkId,
        sessionId,
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        pinId: currentPin.id,
        optionValue: "",
        turnIndex: userCount + 1,
        userMessagesCount: userCount,
        aiMessagesCount: aiCount,
        optionType: "custom"
      });
    }
  }

  function handleCustomInputChange(question: any, value: any) {
    setClarificationResponses(prev => ({
      ...prev,
      [question]: {
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        selected: prev[question]?.selected ?? null,
        custom: value,
        isTypingCustom: true
      }
    }));
  }

  function trackPinSelected(pinId: any, previousPinId: any, pinState: any) {
    if (sessionId && artworkId) {
      trackTransfersPinSelected({
        artworkId,
        sessionId,
        pinId,
        previousPinId: previousPinId ?? null,
        pinState
      });
    }
  }

  function calculateUnansweredPositions() {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    // @ts-expect-error TS(2339): Property 'getBoundingClientRect' does not exist on... Remove this comment to see the full error message
    const containerRect = scrollContainer.getBoundingClientRect();
    let above = 0;
    let below = 0;

    pendingPillClarifications().forEach((clarification: any) => {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);
      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        // @ts-expect-error TS(2339): Property 'querySelector' does not exist on type 'n... Remove this comment to see the full error message
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );
        if (element) {
          const elementRect = element.getBoundingClientRect();
          if (elementRect.bottom < containerRect.top) {
            above += 1;
          } else if (elementRect.top > containerRect.bottom) {
            below += 1;
          }
        }
      }
    });

    setUnansweredAbove(above);
    setUnansweredBelow(below);
  }

  function scrollToFirstUnanswered(direction: any) {
    const scrollContainer = clarificationScrollRef.current;
    if (!scrollContainer) return;

    // @ts-expect-error TS(2339): Property 'getBoundingClientRect' does not exist on... Remove this comment to see the full error message
    const containerRect = scrollContainer.getBoundingClientRect();

    for (const clarification of pendingPillClarifications()) {
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const isUnanswered = !getClarificationAnswer(clarificationResponses[clarification.question]);

      if (isUnanswered) {
        const labelValue = clarification.label || clarification.question;
        // @ts-expect-error TS(2339): Property 'querySelector' does not exist on type 'n... Remove this comment to see the full error message
        const element = scrollContainer.querySelector(
          `[data-clarification-label="${CSS.escape(labelValue)}"]`
        );

        if (element) {
          const elementRect = element.getBoundingClientRect();
          const isTargetDirection =
            direction === "above"
              ? elementRect.bottom < containerRect.top
              : elementRect.top > containerRect.bottom;

          if (isTargetDirection) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            break;
          }
        }
      }
    }
  }

  function formatClarificationResponses(responsesOverride?: any) {
    const responses = responsesOverride ?? clarificationResponses;
    const parts = [];
    const clarifications = [];

    for (const clarification of pendingPillClarifications()) {
      const value = getClarificationAnswer(responses[clarification.question]);
      const isCustom = responses[clarification.question]?.isTypingCustom ?? false;
      if (value) {
        const labelText = clarification.label || clarification.question;
        parts.push(`"${labelText}": ${value}`);
        clarifications.push({
          question: clarification.question,
          value,
          option_type: isCustom ? "custom" : "suggested"
        });
      }
    }

    return { clarificationText: parts.join("\n"), clarifications };
  }

  function dispatchPinMessage({
    sessionId: sid,
    artworkId: aid,
    pinId,
    coordinates,
    message,
    isFirstMessage
  }: any) {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const priorPins = pinsRef.current
      .filter((p: any) => p.id !== pinId)
      .map((p: any) => ({
        pinId: p.id,
        coordinates: p.coordinates,
        state: p.state,
        instruction: p.instruction ?? ""
      }));

    submitPinMessage(
      {
        sessionId: sid,
        pinId,
        coordinates,
        userMessage: message,
        isFirstMessage,
        priorPins
      },
      {
        onAcknowledgment(payload: any) {
          updatePin(pinId, (pin: any) => ({
            ...pin,
            thinkingText: payload.message || undefined
          }));
        },
        onResult(payload: any) {
          const knownStatuses = ["ready", "preview_ready", "needs_clarification", "unsupported"];
          const mappedStatus = knownStatuses.includes(payload.status) ? payload.status : "ready";
          const isNonGenerativeReady = payload.status === "ready" && !payload.generatedImageUrl;
          let nextState = mappedStatus;
          if (isNonGenerativeReady) {
            nextState = "processing";
          } else if (mappedStatus === "preview_ready") {
            nextState = "ready";
          }

          const payloadClarifications = payload.clarifications ?? [];
          const resultMessage = payload.message || "Done.";

          const askingClarifications = payloadClarifications.filter(
            (c: any) => c.status === "asking" && c.options.length > 0
          );
          const hasClarifications =
            nextState === "needs_clarification" && askingClarifications.length > 0;

          updatePin(pinId, (pin: any) => {
            const areasOfInterestUpdate = payload.aoiResult
              ? {
                  areasOfInterest: payload.aoiResult,
                  highlightAreasEnabled: pin.highlightAreasEnabled ?? true
                }
              : {};

            return {
              ...pin,
              state: nextState,
              instruction: payload.instruction,
              thinkingText: undefined,
              ...areasOfInterestUpdate,
              messages: [
                ...pin.messages,
                {
                  id: generateUUID(),
                  role: "ai",
                  content: resultMessage,
                  timestamp: Date.now(),
                  ...(hasClarifications && {
                    clarifications: payloadClarifications,
                    textInfo: payload.text
                  })
                }
              ]
            };
          });

          if (hasClarifications) {
            setClarificationResponses({});
          }

          savedClarificationResponsesRef.current = null;
          savedPinStateRef.current = null;
          savedMessagesRef.current = null;
          savedComposerTextRef.current = null;

          const currentPin = pinsRef.current.find((p: any) => p.id === pinId);
          // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'.
          const userCount = (currentPin?.messages ?? []).filter(
            (m: any) => m.role === "user"
          ).length;
          const aiCount =
            // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'.
            (currentPin?.messages ?? []).filter((m: any) => m.role === "ai").length + 1;
          const trackingData = {
            artworkId: aid,
            sessionId: sid,
            pinId,
            userMessagesCount: userCount,
            aiMessagesCount: aiCount
          };

          if (!sid || !aid) return;

          if (nextState === "ready" || isNonGenerativeReady) {
            trackTransfersPinReady(trackingData);
          } else if (nextState === "unsupported") {
            trackTransfersPinUnsupported(trackingData);
          } else if (nextState === "needs_clarification") {
            trackTransfersPinNeedsClarification({
              ...trackingData,
              optionsCount: askingClarifications.reduce(
                (sum: any, c: any) => sum + c.options.length,
                0
              ),
              questionType: askingClarifications.map((c: any) => c.type).join(",")
            });
          }

          if (payload.status === "preview_ready" && payload.generatedImageUrl) {
            setIsPreviewImageLoaded(false);
            setPreviewApproval({
              pinId,
              generatedImageUrl: payload.generatedImageUrl,
              instruction: payload.instruction || undefined
            });
            setIsPanelExpanded(false);
            trackPinSessionGeneratedImageShown({
              artworkId: aid,
              sessionId: sid,
              generatedImageUrl: payload.generatedImageUrl,
              pinId
            });
          }
        },
        onStreamEnd() {
          // Use functional updater to read the latest pin state —
          // onResult may have queued a setPins update (e.g. preview_ready)
          // that hasn't committed to pinsRef.current yet.
          // @ts-expect-error TS(2345): Argument of type '(prev: never[]) => any[]' is not... Remove this comment to see the full error message
          setPins((prev: any[]) => {
            const pin = prev.find((p: any) => p.id === pinId);
            if (pin?.state === "processing") {
              if (sid && aid) {
                trackTransfersArtistEditEscalationShown({
                  artworkId: aid,
                  sessionId: sid,
                  pinId
                });
              }
              return prev.map((p: any) => (p.id === pinId ? { ...p, state: "ready" } : p));
            }
            return prev;
          });
        },
        onError() {
          // Use functional updater for tracking — pinsRef.current may be stale
          const latestPins = pinsRef.current;
          const currentPin = latestPins.find((p: any) => p.id === pinId);
          // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'.
          const userCount = (currentPin?.messages ?? []).filter(
            (m: any) => m.role === "user"
          ).length;
          // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'.
          const aiCount = (currentPin?.messages ?? []).filter((m: any) => m.role === "ai").length;

          if (sid && aid) {
            trackTransfersPinError({
              artworkId: aid,
              sessionId: sid,
              pinId,
              errorType: "processing_error",
              userMessagesCount: userCount,
              aiMessagesCount: aiCount
            });
          }

          const previousState = savedPinStateRef.current ?? "draft";
          const previousMessages = savedMessagesRef.current;

          updatePin(pinId, (pin: any) => ({
            ...pin,
            state: previousState,
            thinkingText: undefined,
            messages: previousMessages ?? pin.messages.slice(0, -1)
          }));

          if (savedClarificationResponsesRef.current) {
            setClarificationResponses(savedClarificationResponsesRef.current);
            savedClarificationResponsesRef.current = null;
          }

          if (savedComposerTextRef.current !== null) {
            setComposerText(savedComposerTextRef.current);
            savedComposerTextRef.current = null;
          }

          savedPinStateRef.current = null;
          savedMessagesRef.current = null;

          toast?.error({ title: "Something went wrong", description: "Please try again." });
        }
      },
      {
        isPinActive: (id: any) =>
          pinsRef.current.some((p: any) => p.id === id) && !removingPinIdsRef.current.has(id),
        signal: abortControllerRef.current.signal
      }
    );
  }

  function handleSubmit(responsesOverride?: any) {
    const currentActivePin = activePin();
    if (!currentActivePin) return;
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    if (currentActivePin.state === "processing") return;

    const { clarificationText, clarifications } = formatClarificationResponses(responsesOverride);
    const messageText = composerText.trim();
    const fullMessage = [clarificationText, messageText].filter(Boolean).join("\n");

    if (!fullMessage) return;

    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    const pinId = currentActivePin.id;
    const userMessageId = generateUUID();
    const isFirstMessage =
      // @ts-expect-error TS migration
      currentActivePin.messages.filter((m: any) => m.role === "user").length === 0;
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    const turnIndex = currentActivePin.messages.filter((m: any) => m.role === "user").length + 1;
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    const userMessagesCount = currentActivePin.messages.filter(
      (m: any) => m.role === "user"
    ).length;
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    const aiMessagesCount = currentActivePin.messages.filter((m: any) => m.role === "ai").length;

    if (sessionId && artworkId) {
      trackTransfersPinMessageSubmitted({
        artworkId,
        sessionId,
        pinId,
        isFirstMessage,
        turnIndex,
        userMessagesCount,
        aiMessagesCount,
        clarifications
      });
    }

    savedClarificationResponsesRef.current = { ...(responsesOverride ?? clarificationResponses) };
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    savedPinStateRef.current = currentActivePin.state;
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    savedMessagesRef.current = [...currentActivePin.messages];
    savedComposerTextRef.current = composerText;

    updatePin(pinId, (pin: any) => ({
      ...pin,
      state: "processing",
      thinkingText: undefined,

      messages: [
        ...pin.messages.map((m: any) => ({
          ...m,
          clarifications: undefined
        })),
        { id: userMessageId, role: "user", content: fullMessage, timestamp: Date.now() }
      ]
    }));

    setComposerText("");
    setClarificationResponses({});
    setLastEditedPinId(pinId);

    if (!sessionId) return;

    dispatchPinMessage({
      sessionId,
      artworkId,
      pinId,
      // @ts-expect-error TS(2339): Property 'coordinates' does not exist on type 'nev... Remove this comment to see the full error message
      coordinates: currentActivePin.coordinates,
      message: fullMessage,
      isFirstMessage
    });
  }

  useEffect(() => {
    if (mobileView === "list") {
      setIsPanelExpanded(false);
      setHoveredPinId(null);
      setHoveredPinSource(null);
    }
  }, [mobileView]);

  useEffect(() => {
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    if (messagesScrollRef.current && activePin()?.messages.length) {
      setTimeout(() => {
        if (messagesScrollRef.current) {
          // @ts-expect-error TS(2339): Property 'scrollTop' does not exist on type 'never... Remove this comment to see the full error message
          messagesScrollRef.current.scrollTop = messagesScrollRef.current.scrollHeight;
        }
      }, 0);
    }
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
  }, [activePin()?.messages.length]);

  useEffect(() => {
    const active = activePin();
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    if (active?.state === "ready") {
      setIsPanelExpanded(false);
    }
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
  }, [activePin()?.state]);

  useEffect(() => {
    setComposerText("");
    setClarificationResponses({});
  }, [activePinId]);

  useEffect(() => {
    if (pendingCursorPosition !== null && textareaRef.current) {
      setTimeout(() => {
        const pos = pendingCursorPosition;
        if (textareaRef.current && pos !== null) {
          // @ts-expect-error TS(2339): Property 'selectionStart' does not exist on type '... Remove this comment to see the full error message
          textareaRef.current.selectionStart = pos;
          // @ts-expect-error TS(2339): Property 'selectionEnd' does not exist on type 'ne... Remove this comment to see the full error message
          textareaRef.current.selectionEnd = pos;
          // @ts-expect-error TS(2339): Property 'scrollTop' does not exist on type 'never... Remove this comment to see the full error message
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
        setPendingCursorPosition(null);
      }, 0);
    }
  }, [composerText, pendingCursorPosition]);

  useEffect(() => {
    if (pendingPillClarifications().length > 0) {
      calculateUnansweredPositions();
    }
  }, [pendingPillClarifications().length, clarificationResponses]);

  useEffect(() => {
    if (pins.length === 0) return;

    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    const incompletePin = pins.find(p => p.state !== "ready" && p.state !== "unsupported");
    if (!incompletePin) return;

    if (isDesktop) {
      if (!activePinId) {
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        setActivePinId(incompletePin.id);
        setTimeout(() => {
          // @ts-expect-error TS(2339): Property 'querySelector' does not exist on type 'n... Remove this comment to see the full error message
          const pinElement = pinsContainerRef.current?.querySelector(
            `[data-pin-id="${
              // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
              incompletePin.id
            }"]`
          );
          pinElement?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    } else if (mobileView === "list") {
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
      setActivePinId(incompletePin.id);
      setMobileView("detail");
      setIsPanelExpanded(true);
    }
  }, [pins, mobileView, activePinId, isDesktop]);

  useEffect(() => {
    if (!isDesktop && pins.length === 0) {
      setMobileView("list");
    }
  }, [pins.length, isDesktop]);

  useEffect(() => {
    const active = activePin();
    if (!isDesktop && mobileView === "detail" && active) {
      // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
      const isCompleted = active.state === "ready" || active.state === "unsupported";
      if (isCompleted) {
        // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
        setHoveredPinId(active.id);
        // @ts-expect-error TS(2345): Argument of type '"panel"' is not assignable to pa... Remove this comment to see the full error message
        setHoveredPinSource("panel");
      }
    }
  }, [mobileView, activePin(), isDesktop]);

  useEffect(() => {
    if (!isDesktop && activePinId) {
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
      const pin = pins.find(p => p.id === activePinId);
      // @ts-expect-error TS(2339): Property 'isNew' does not exist on type 'never'.
      if (pin?.isNew && pin?.state === "draft" && pin?.messages.length === 0) {
        setMobileView("detail");
        setIsPanelExpanded(true);
      }
    }
  }, [activePinId, pins, isDesktop]);

  const autoSubmitTriggeredRef = useRef(false);

  useEffect(() => {
    if (!pendingInitialMessage) return;
    if (autoSubmitTriggeredRef.current) return;
    if (pins.length === 0 || !activePinId) return;

    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    const pin = pins.find(p => p.id === activePinId);
    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    if (!pin || pin.state !== "draft" || pin.messages.length > 0) return;

    if (!sessionId) return;

    autoSubmitTriggeredRef.current = true;
    const message = pendingInitialMessage;

    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'never'.
    const pinId = pin.id;
    const userMessageId = generateUUID();

    // @ts-expect-error TS(2339): Property 'state' does not exist on type 'never'.
    savedPinStateRef.current = pin.state;
    // @ts-expect-error TS(2339): Property 'messages' does not exist on type 'never'... Remove this comment to see the full error message
    savedMessagesRef.current = [...pin.messages];
    savedComposerTextRef.current = message;

    updatePin(pinId, (p: any) => ({
      ...p,
      state: "processing",
      thinkingText: undefined,
      messages: [{ id: userMessageId, role: "user", content: message, timestamp: Date.now() }]
    }));
    setLastEditedPinId(pinId);

    if (artworkId && sessionId) {
      trackTransfersPinMessageSubmitted({
        artworkId,
        sessionId,
        pinId,
        isFirstMessage: true,
        turnIndex: 1,
        userMessagesCount: 1,
        aiMessagesCount: 0,
        clarifications: []
      });
    }

    dispatchPinMessage({
      sessionId,
      artworkId,
      pinId,
      // @ts-expect-error TS(2339): Property 'coordinates' does not exist on type 'nev... Remove this comment to see the full error message
      coordinates: pin.coordinates,
      message,
      isFirstMessage: true
    });
  }, [pins, activePinId, sessionId, artworkId, pendingInitialMessage]);

  function hydratePins(pinsData: any) {
    setPins(
      pinsData.map((p: any) => {
        const messages = Array.isArray(p.messages)
          ? p.messages
              .filter((m: any) => m?.role !== "ai_ack")
              .map((m: any) => ({
                id: m.id || "",
                role: m.role || "user",
                content: m.content || "",
                timestamp: m.timestamp || Date.now(),
                ...(m.options ? { options: m.options } : {}),
                ...(m.questionType ? { questionType: m.questionType } : {}),
                ...(m.clarifications ? { clarifications: m.clarifications } : {}),
                ...(m.textInfo ? { textInfo: m.textInfo } : {})
              }))
          : [];

        if (messages.length === 0 && p.message) {
          messages.push({
            id: `msg-${p.pinId}`,
            role: "ai",
            content: p.message,
            timestamp: Date.now()
          });
        }

        const state = p.status || p.state || "draft";

        return {
          id: p.pinId || p.id || "",
          coordinates: p.coordinates || { x: 0, y: 0 },
          state,
          instruction: p.instruction || p.summary,
          messages,
          areasOfInterest: p.areaOfInterest,
          segmentationMaskUrl: p.segmentationMaskUrl,
          isNew: false
        };
      })
    );

    setActivePinId(null);
  }

  async function handleApprovePreview() {
    if (!previewApproval || isApproving || isRejecting) return null;
    setIsApproving(true);
    try {
      return await approvePinApi(sessionId);
    } catch (err) {
      setIsApproving(false);
      toast?.error?.({ title: "Failed to approve", description: "Please try again." });
      return null;
    }
  }

  async function handleRejectPreview() {
    if (!previewApproval || isRejecting || isApproving) return null;
    setIsRejecting(true);
    try {
      return await rejectPinApi(sessionId);
    } catch (err) {
      setIsRejecting(false);
      toast?.error?.({ title: "Failed to reject", description: "Please try again." });
      return null;
    }
  }

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      setPins([]);
      setActivePinId(null);
      setLastEditedPinId(null);
    },
    []
  );

  return {
    PIN_COLORS,
    MAX_PINS,

    isDesktop,
    pins,
    setPins,
    activePinId,
    setActivePinId,
    hoveredPinId,
    setHoveredPinId,
    hoveredPinSource,
    setHoveredPinSource,
    composerText,
    setComposerText,
    removingPinIds,
    pendingDeletePinId,
    clarificationResponses,
    mobileView,
    setMobileView,
    isPanelExpanded,
    setIsPanelExpanded,
    editingPinId,
    isUpdatingPinInstruction,
    lastEditedPinId,
    unansweredAbove,
    unansweredBelow,
    showPins,
    setShowPins,
    previewApproval,
    setPreviewApproval,
    isApproving,
    setIsApproving,
    isRejecting,
    isPreviewImageLoaded,
    setIsPreviewImageLoaded,

    textareaRef,
    pendingCursorPosition,
    setPendingCursorPosition,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,

    activePin,
    editingPin,
    hasCompletedPin,
    readyPinsCount,
    hasExistingPin,
    hasAiAssistanceOngoing,
    hasUnfinishedPin,
    canAddNewPin,
    canRepositionActivePin,
    lastAiMessageWithClarifications,
    pendingPillClarifications,
    fontClarification,
    colorClarification,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,

    addPin,
    repositionActivePin,
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    trackPinSelected,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    hydratePins,
    handleApprovePreview,
    handleRejectPreview
  };
}
